import { format } from 'date-fns';
import { createClient } from './supabase';
import { productService } from './product.service';
import { handleError } from './error-handler';

// Food Donation Interfaces
export interface FoodDonation {
  id: string;
  business_id: string;
  business_name: string;
  date: string;
  items: FoodDonationItem[];
  status: 'pending' | 'accepted' | 'picked_up' | 'cancelled';
  totalValue: number;
  end_time?: string; // When product auction/availability ended
  expiry_date?: string; // Product expiry date
}

export interface FoodDonationItem {
  id: string;
  name: string;
  quantity: number;
  originalPrice: number;
  discountPrice: number;
  product_id?: string; // Reference to original product
  category?: string;  // Product category
  end_time?: string;  // When product availability ended
  expiry_date?: string; // Product expiry date
}

// Monetary Donation Interfaces
export interface MonetaryDonation {
  id: string;
  donor_id: string;
  donor_name: string;
  date: string;
  amount: number;
  message?: string;
  isAnonymous: boolean;
  charity_id?: string;
}

// Donation Statistics Interfaces
export interface DonationStatistics {
  totalReceived: number;
  totalValue: number;
  growth: number;
  monetaryTotal: number;
  monetaryGrowth: number;
}

export interface MonetaryDonationStatistics {
  totalDonations: number;
  thisMonthDonations: number;
  lastMonthDonations: number;
  growth: number;
}

// Filter types
export type DonationFilter = {
  searchTerm?: string;
  status?: 'all' | 'pending' | 'accepted' | 'picked_up' | 'cancelled';
  dateRange?: {
    start: Date;
    end: Date;
  }
};

export type MonetaryDonationFilter = {
  searchTerm?: string;
  dateRange?: {
    start: Date;
    end: Date;
  }
};

export class DonationsService {
  // Get Food Donations
  static async getFoodDonations(charityId: string, filter?: DonationFilter): Promise<FoodDonation[]> {
    try {
      const supabase = await createClient();
      
      // Start building the query
      let query = supabase
        .from('food_donations')
        .select(`
          id,
          created_at,
          status,
          businesses!inner(id, name),
          food_donation_items(id, name, quantity, original_price, discount_price, product_id, category, end_time, expiry_date)
        `);
      
      // Apply filters
      query = query.eq('charity_id', charityId);
      
      if (filter?.status && filter.status !== 'all') {
        query = query.eq('status', filter.status);
      }
      
      if (filter?.dateRange) {
        query = query
          .gte('created_at', filter.dateRange.start.toISOString())
          .lte('created_at', filter.dateRange.end.toISOString());
      }
      
      // Execute the query
      const { data, error } = await query;
      
      if (error) throw error;
      if (!data) return [];
      
      // For a complete implementation, we could also fetch recently ended products
      // that haven't expired to suggest as potential food donations
      await productService.getAll({  
        status: 'expired', // Products that are no longer available but not past expiry
        business_id: undefined // We could filter by business_id if needed
      });
      // The products could be used to suggest potential donations
      
      // Transform the data to match the FoodDonation interface
      const foodDonations: FoodDonation[] = data.map((item: any) => ({
        id: item.id,
        business_id: item.businesses?.id || '',
        business_name: item.businesses?.name || '',
        date: item.created_at,
        items: (item.food_donation_items || []).map((donationItem: any) => ({
          id: donationItem.id,
          name: donationItem.name,
          quantity: donationItem.quantity,
          originalPrice: donationItem.original_price,
          discountPrice: donationItem.discount_price,
          product_id: donationItem.product_id,
          category: donationItem.category,
          end_time: donationItem.end_time,
          expiry_date: donationItem.expiry_date
        })),
        status: item.status,
        totalValue: (item.food_donation_items || []).reduce((sum: number, donationItem: any) => 
          sum + (donationItem.original_price * donationItem.quantity), 0),
        // We could potentially add the end_time and expiry_date here if the donation has a single item
        // or derive it from the earliest expiry date among all items
        end_time: item.food_donation_items?.[0]?.end_time,
        expiry_date: item.food_donation_items?.[0]?.expiry_date
      }));
      
      // Apply text search filter if provided (since Supabase doesn't support full-text search in the same way)
      if (filter?.searchTerm) {
        return this.filterFoodDonations(foodDonations, { searchTerm: filter.searchTerm });
      }
      
      return foodDonations;
    } catch (error) {
      console.error('Error fetching food donations:', error);
      throw error;
    }
  }

  // Get Monetary Donations
  static async getMonetaryDonations(charityId: string, filter?: MonetaryDonationFilter): Promise<MonetaryDonation[]> {
    try {
      const supabase = await createClient();
      
      // Start building the query
      let query = supabase
        .from('monetary_donations')
        .select(`
          id,
          created_at,
          amount,
          message,
          is_anonymous,
          donor_id,
          profiles(id, full_name)
        `);
      
      // Apply filters
      query = query.eq('charity_id', charityId);
      
      if (filter?.dateRange) {
        query = query
          .gte('created_at', filter.dateRange.start.toISOString())
          .lte('created_at', filter.dateRange.end.toISOString());
      }
      
      // Execute the query
      const { data, error } = await query;
      
      if (error) throw error;
      if (!data) return [];
      
      // Transform the data to match the MonetaryDonation interface
      const monetaryDonations: MonetaryDonation[] = data.map((item: any) => {
        const isAnonymous = item.is_anonymous || false;
        let donorName = 'Anonymous';
        
        // If not anonymous and we have profile data, use the profile name
        if (!isAnonymous && item.profiles) {
          donorName = item.profiles.full_name || 'Unknown';
        }
        
        return {
          id: item.id,
          donor_id: item.donor_id,
          donor_name: donorName,
          date: item.created_at,
          amount: item.amount,
          message: item.message,
          isAnonymous: isAnonymous,
          charity_id: charityId
        };
      });
      
      // Apply text search filter if provided
      if (filter?.searchTerm) {
        return this.filterMonetaryDonations(monetaryDonations, { searchTerm: filter.searchTerm });
      }
      
      return monetaryDonations;
    } catch (error) {
      console.error('Error fetching monetary donations:', error);
      return handleError(error);
    }
  }

  // Get Food Donation Statistics
  static async getFoodDonationStatistics(charityId: string): Promise<{
    totalReceived: number;
    totalValue: number;
    growth: number;
  }> {
    try {
      const supabase = await createClient();
      
      // Call Supabase function to get food donation statistics
      const { data, error } = await supabase
        .rpc('get_charity_donation_statistics', { charity_id: charityId });
      
      if (error) throw error;
      if (!data) {
        return {
          totalReceived: 0,
          totalValue: 0,
          growth: 0
        };
      }
      
      // Extract the relevant statistics from the returned data
      return {
        totalReceived: data.total_food_donations || 0,
        totalValue: data.total_food_value || 0,
        growth: data.food_growth_percentage || 0
      };
    } catch (error) {
      console.error('Error fetching food donation statistics:', error);
      // Return default values if there's an error
      return {
        totalReceived: 0,
        totalValue: 0,
        growth: 0
      };
    }
  }

  // Get Monetary Donation Statistics
  static async getMonetaryDonationStatistics(charityId: string): Promise<MonetaryDonationStatistics> {
    try {
      const supabase = await createClient();
      
      // Call Supabase function to get monetary donation statistics
      const { data, error } = await supabase
        .rpc('get_charity_donation_statistics', { charity_id: charityId });
      
      if (error) throw error;
      
      // Default values if no data is returned
      if (!data) {
        return {
          totalDonations: 0,
          thisMonthDonations: 0,
          lastMonthDonations: 0,
          growth: 0
        };
      }

      // Extract the relevant statistics from the returned data
      return {
        totalDonations: data.total_monetary_donations || 0,
        thisMonthDonations: data.this_month_monetary_donations || 0,
        lastMonthDonations: data.last_month_monetary_donations || 0,
        growth: data.monetary_growth_percentage || 0
      };
    } catch (error) {
      console.error('Error fetching monetary donation statistics:', error);
      // Return default values if there's an error
      return {
        totalDonations: 0,
        thisMonthDonations: 0,
        lastMonthDonations: 0,
        growth: 0
      };
    }
  }

  // Get Combined Donation Statistics
  static async getDonationStatistics(charityId: string): Promise<DonationStatistics> {
    try {
      const supabase = await createClient();
      
      // Call Supabase function to get all donation statistics at once
      const { data, error } = await supabase
        .rpc('get_charity_donation_statistics', { charity_id: charityId });
      
      if (error) throw error;
      
      if (!data) {
        return {
          totalReceived: 0,
          totalValue: 0,
          growth: 0,
          monetaryTotal: 0,
          monetaryGrowth: 0
        };
      }
      
      // Extract the relevant statistics from the returned data
      return {
        totalReceived: data.total_food_donations || 0,
        totalValue: data.total_food_value || 0,
        growth: data.food_growth_percentage || 0,
        monetaryTotal: data.total_monetary_donations || 0,
        monetaryGrowth: data.monetary_growth_percentage || 0
      };
    } catch (error) {
      console.error('Error fetching combined donation statistics:', error);
      // Return default values if there's an error
      return {
        totalReceived: 0,
        totalValue: 0,
        growth: 0,
        monetaryTotal: 0,
        monetaryGrowth: 0
      };
    }
  }

  // Filter Food Donations helper
  private static filterFoodDonations(donations: FoodDonation[], filter: DonationFilter): FoodDonation[] {
    return donations.filter(donation => {
      // Apply search term filter
      if (filter.searchTerm) {
        const searchTerm = filter.searchTerm.toLowerCase();
        const matchesSearch =
          donation.business_name.toLowerCase().includes(searchTerm) ||
          donation.id.toLowerCase().includes(searchTerm) ||
          donation.items.some(item => item.name.toLowerCase().includes(searchTerm));

        if (!matchesSearch) return false;
      }

      // Apply status filter
      if (filter.status && filter.status !== 'all' && donation.status !== filter.status) {
        return false;
      }

      // Apply date range filter
      if (filter.dateRange) {
        const donationDate = new Date(donation.date);
        if (
          donationDate < filter.dateRange.start ||
          donationDate > filter.dateRange.end
        ) {
          return false;
        }
      }

      return true;
    });
  }

  // Filter Monetary Donations helper
  private static filterMonetaryDonations(donations: MonetaryDonation[], filter: MonetaryDonationFilter): MonetaryDonation[] {
    return donations.filter(donation => {
      // Apply search term filter
      if (filter.searchTerm) {
        const searchTerm = filter.searchTerm.toLowerCase();
        const donorName = donation.isAnonymous ? 'Anonymous' : donation.donor_name;
        const matchesSearch =
          donorName.toLowerCase().includes(searchTerm) ||
          donation.id.toLowerCase().includes(searchTerm) ||
          (donation.message && donation.message.toLowerCase().includes(searchTerm));

        if (!matchesSearch) return false;
      }

      // Apply date range filter
      if (filter.dateRange) {
        const donationDate = new Date(donation.date);
        if (
          donationDate < filter.dateRange.start ||
          donationDate > filter.dateRange.end
        ) {
          return false;
        }
      }

      return true;
    });
  }

  // Format date helper
  static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return format(date, 'MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }

  // Format currency helper
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }
}
